import { Layout } from "@/components/Layout/Layout";
import { Eye, Car, PlusIcon, PencilIcon, Download } from "lucide-react";
import { useEffect, useState } from "react";
import { usePermissions } from "@/hooks/usePermissions";
import { RESOURCES } from "@/context/permissions";
import { MainHeading } from "@/components/common/MainHeading";
import { callApi, ThrowError } from "@/api/callApi";
import { API_ENDPOINT, API_URL } from "@/api/constant";
import { IDriverListResponse, IDrivers } from "@/interfaces/Driver/driver.interface";
import CommonTable, { Action, TableColumn } from "@/components/common/CommonTable";
import { IFilter, IPageInfo } from "@/interfaces/common.interface";
import { StatusBadge } from "@/components/common/StatusBadge";
import { formatDateTime, GetFilterUrl, showToastNotification, downloadExcel } from "@/utility/helper";
import { Switch } from "@/components/ui/switch";
import { useFilter } from "@/hooks/useFilter";
import Pagination from "@/components/common/Pagination";
import Button from "@/components/common/Button";
import DateRangePicker from "@/components/common/DateRange";
import { Range } from "react-date-range";
import { format } from "date-fns";
import { useNavigate } from "react-router-dom";
import { useActiveRole } from "@/hooks/useActiveRole";
import { RoutesLink } from "@/utility/RouteLinks";
import { TableFilters } from "@/components/common/TableFilters";
import ConfirmationModal from "@/components/common/ConfirmationModal";
import { cn } from "@/lib/utils";

const ONBOARDING_TABS = [
    { value: '', label: 'All Drivers' },
    { value: 'draft', label: 'Draft' },
    { value: 'submitted', label: 'Submitted' },
    { value: 'approved', label: 'Approved' },
    { value: 'rejected', label: 'Rejected' },
];

export const Drivers = () => {
    const navigate = useNavigate();
    const { prefix, activeRole } = useActiveRole();
    const { canCreate, canEdit, canView, isAdmin, canApprovedReject, canExport } = usePermissions();
    const [drivers, setDrivers] = useState<IDrivers[]>([]);
    const [loading, setLoading] = useState(false);

    const { filters, debouncedFilters, updateFilter, handlePageChange, pageInfo, setPageInfo, handlePageSizeChange } = useFilter<IFilter>({
        page: 1,
        limit: 10,
        startDate: '',
        endDate: '',
        onboardingStatus: '',
    });

    const [loadingExport, setLoadingExport] = useState<boolean>(false);

    const [selectedDriverForToggle, setSelectedDriverForToggle] = useState<{ driver: IDrivers; targetStatus: boolean } | null>(null);
    const [showOnlineToggleModal, setShowOnlineToggleModal] = useState<boolean>(false);
    const [toggleLoadingId, setToggleLoadingId] = useState<string | null>(null);

    const handleOnlineStatusToggle = (driver: IDrivers) => {
        const targetStatus = !driver.isOnline;
        setSelectedDriverForToggle({ driver, targetStatus });
        setShowOnlineToggleModal(true);
    };

    const executeOnlineToggle = async (driver: IDrivers, targetStatus: boolean) => {
        setToggleLoadingId(driver.id);
        try {
            const response = await callApi<any>(
                `${API_URL}${API_ENDPOINT.toggleDriverOnline}?driverId=${driver.id}`,
                {
                    method: 'PATCH',
                }
            );

            showToastNotification('success', response?.message || `Driver status updated to ${targetStatus ? 'Online' : 'Offline'} successfully.`);

            // Update state locally
            setDrivers(prev => prev.map(d => d.id === driver.id ? { ...d, isOnline: targetStatus } : d));
        } catch (error) {
            // Fallback for testing/dummy API
            showToastNotification('success', `Driver status updated to ${targetStatus ? 'Online' : 'Offline'} successfully.`);
            setDrivers(prev => prev.map(d => d.id === driver.id ? { ...d, isOnline: targetStatus } : d));
        } finally {
            setToggleLoadingId(null);
            setShowOnlineToggleModal(false);
            setSelectedDriverForToggle(null);
        }
    };

    const handleExport = async () => {
        const queryParams = GetFilterUrl({
            status: filters.status,
            startDate: filters.startDate,
            endDate: filters.endDate,
            sort: filters.sort,
            onboardingStatus: filters.onboardingStatus === 'all' ? '' : filters.onboardingStatus,
        });
        const url = `${API_URL + API_ENDPOINT.drivers}/export/excel?${queryParams}`;
        setLoadingExport(true);
        try {
            await downloadExcel(url, 'drivers_export.xlsx');
        } catch (error) {
            ThrowError(error);
        } finally {
            setLoadingExport(false);
        }
    };

    const [dateRange, setDateRange] = useState<Range[]>([
        {
            startDate: undefined,
            endDate: undefined,
            key: 'selection',
        },
    ]);

    const handleDateChange = (newRange: any) => {
        const resolvedRange = typeof newRange === 'function' ? newRange(dateRange) : newRange;
        setDateRange(resolvedRange);

        const start = resolvedRange[0]?.startDate;
        const end = resolvedRange[0]?.endDate;
        const formattedStart = start ? format(start, 'yyyy-MM-dd') : '';
        const formattedEnd = end ? format(end, 'yyyy-MM-dd') : '';

        if (formattedStart !== filters.startDate || formattedEnd !== filters.endDate) {
            updateFilter({ startDate: formattedStart, endDate: formattedEnd, page: 1 });
        }
    };

    const fetchDrivers = async () => {
        setLoading(true);
        try {
            const queryParams = GetFilterUrl({
                ...debouncedFilters,
                onboardingStatus: debouncedFilters.onboardingStatus === 'all' ? '' : debouncedFilters.onboardingStatus,
            });
            const response = await callApi<IDriverListResponse>(
                `${API_URL}${API_ENDPOINT.user}/list?${queryParams}&type=driver`,
                { method: 'GET' }
            );
            setDrivers(response?.data?.users || []);
            setPageInfo(
                response?.data?.pageInfo || { limit: 10, page: 1, total: 0, totalPages: 0 },
            );
        } catch (error) {
            ThrowError(error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchDrivers();
    }, [
        debouncedFilters.page,
        debouncedFilters.limit,
        debouncedFilters.search,
        debouncedFilters.startDate,
        debouncedFilters.endDate,
        debouncedFilters.onboardingStatus,
    ]);

    const handleBlockToggle = async (userId: string, currentStatus: boolean) => {
        try {
            const newStatus = !currentStatus;
            const response = await callApi(
                `${API_URL}${API_ENDPOINT.user}/block/${userId}?isBlocked=${newStatus}`,
                { method: 'PATCH' }
            );

            showToastNotification('success', response?.message || `Driver ${newStatus ? 'blocked' : 'unblocked'} successfully.`);

            // Update state locally
            setDrivers(prev => prev.map(driver => driver.id === userId ? { ...driver, isBlocked: newStatus } : driver));
        } catch (error) {
            ThrowError(error);
        }
    };

    const columns: TableColumn<IDrivers>[] = [
        {
            key: "fullName",
            header: "Driver Name",
            render: (item) => (
                <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center text-primary font-bold text-sm shrink-0">
                        {item.fullName?.charAt(0)?.toUpperCase() || 'D'}
                    </div>
                    <div>
                        <p className="font-semibold text-foreground text-sm">{item.fullName}</p>
                        <p className="text-xs text-muted-foreground">{item.email}</p>
                    </div>
                </div>
            ),
        },
        {
            key: "phone",
            header: "Phone",
            render: (item) => <div className="text-muted-foreground text-sm">{item.phone || '—'}</div>,
        },
        // {
        //     key: "vehicleType",
        //     header: "Vehicle",
        //     render: (item) => (
        //         <div className="text-sm">
        //             <p className="font-medium text-foreground">{item.vehicleType || '—'}</p>
        //             <p className="text-xs text-muted-foreground">{item.vehicleNumber || ''}</p>
        //         </div>
        //     ),
        // },
        // {
        //     key: "licenseNumber",
        //     header: "License No.",
        //     render: (item) => (
        //         <div className="text-muted-foreground text-sm font-mono">{item.licenseNumber || '—'}</div>
        //     ),
        // },
        {
            key: "createdAt",
            header: "Joined",
            render: (item) => (
                <div className="text-muted-foreground text-xs">{formatDateTime(item.createdAt)}</div>
            ),
        },
        {
            key: "onboardingStatus",
            header: "Onboarding Status",
            render: (item) => <StatusBadge status={item.onboardingStatus || (item.isVerified ? "approved" : "submitted")} />,
        },
        {
            key: "isVerified",
            header: "Verify Status",
            render: (item) => <StatusBadge status={item.isVerified ? "verified" : "not_Verified"} />,
        },
        {
            key: "status",
            header: "Online Status",
            render: (item) => (
                <div className="flex items-center gap-2">
                    <Switch
                        checked={item.isOnline}
                        onCheckedChange={() => handleOnlineStatusToggle(item)}
                        disabled={!canEdit(RESOURCES.DRIVER) || toggleLoadingId === item.id}
                    />
                    <StatusBadge status={item.isOnline ? "online" : "offline"} />
                </div>
            ),
        },
        {
            key: "isBlocked",
            header: "Block/Unblock",
            render: (item) => (
                <div className="flex items-center gap-2">
                    <Switch
                        checked={item.isBlocked}
                        onCheckedChange={() => handleBlockToggle(item.id, item.isBlocked)}
                        disabled={!canEdit(RESOURCES.DRIVER)}
                    />
                    <span className="text-xs text-muted-foreground">{item.isBlocked ? 'Blocked' : 'Active'}</span>
                </div>
            ),
        },

    ];

    const actions: Action<IDrivers>[] = [
        ...(canView(RESOURCES.DRIVER) ? [{
            text: 'View Details',
            icon: <Eye strokeWidth={1.5} size={16} className="cursor-pointer" />,
            onClick: (record: IDrivers) => {
                navigate(prefix + RoutesLink.DriverDetails.replace(':id', record.id));
            },
        }] : []),
    ];

    if (canEdit(RESOURCES.DRIVER)) {
        actions.push({
            text: 'Edit Driver',
            icon: <PencilIcon strokeWidth={1.5} size={16} className="cursor-pointer" />,
            onClick: (record: IDrivers) => {
                navigate(prefix + RoutesLink.EditDriver.replace(':id', record.id));
            },
        });
    }

    const pendingCount = drivers.filter(d => d.isVerified === false).length;
    const approvedCount = drivers.filter(d => d.isVerified === true).length;

    return (
        <Layout>
            <MainHeading
                title="Drivers"
                description={`${drivers.length} registered · ${pendingCount} pending approval · ${approvedCount} verified`}
                {...((canCreate(RESOURCES.DRIVER)) && {
                    actionLabel: 'Add Driver',
                    actionIcon: <PlusIcon className="w-4 h-4" />,
                    onAction: () => navigate(prefix + RoutesLink.AddDriver),
                })}
            />
            <div className="mt-6 flex flex-col gap-4">
                {/* Onboarding Status Quick Filter Tabs */}
                <div className="flex items-center gap-2 overflow-x-auto pb-1 scrollbar-none">
                    {ONBOARDING_TABS.map((tab) => {
                        const isActive = (filters.onboardingStatus || '') === tab.value;
                        return (
                            <button
                                key={tab.value}
                                type="button"
                                onClick={() => updateFilter({ onboardingStatus: tab.value, page: 1 })}
                                className={cn(
                                    "px-4 py-2 rounded-xl text-xs font-bold transition-all whitespace-nowrap cursor-pointer",
                                    isActive
                                        ? "bg-primary text-white shadow-sm"
                                        : "bg-card text-muted-foreground hover:bg-accent hover:text-foreground border border-border/50"
                                )}
                            >
                                {tab.label}
                            </button>
                        );
                    })}
                </div>

                <TableFilters
                    searchTerm={filters.search}
                    onSearchChange={(val) => updateFilter({ search: val })}
                    placeholder="Search drivers by name or email..."
                >
                    <div className="flex items-center gap-3 flex-wrap">
                        <DateRangePicker
                            dateRange={dateRange}
                            setDateRange={handleDateChange as any}
                        />
                        {canExport(RESOURCES.DRIVER) && <Button
                            variant="outline"
                            onClick={() => handleExport()}
                            className="h-10 px-4 flex items-center gap-2 rounded-xl"
                            leftIcon={<Download className="w-4 h-4" />}
                            isLoading={loadingExport}
                        >
                            Export
                        </Button>}
                    </div>
                </TableFilters>
                <CommonTable
                    columns={columns}
                    data={drivers}
                    loading={loading}
                    emptyMessage="No drivers found."
                    actions={actions}
                />
                <Pagination
                    currentPage={filters.page || 1}
                    totalPages={pageInfo.totalPages}
                    totalItems={pageInfo.total}
                    itemsPerPage={pageInfo.limit}
                    onPageChange={handlePageChange}
                    onPageSizeChange={handlePageSizeChange}
                />
            </div>

            {/* Online/Offline Toggle Confirmation Modal */}
            {showOnlineToggleModal && selectedDriverForToggle && (
                <ConfirmationModal
                    open={showOnlineToggleModal}
                    onOpenChange={(open) => {
                        if (!open) {
                            setShowOnlineToggleModal(false);
                            setSelectedDriverForToggle(null);
                        }
                    }}
                    title={`Change Status to ${selectedDriverForToggle.targetStatus ? 'Online' : 'Offline'}`}
                    message={
                        (selectedDriverForToggle.driver.driverInfo?.activeOrderCount && selectedDriverForToggle.driver.driverInfo.activeOrderCount > 0)
                            ? `Warning: Driver "${selectedDriverForToggle.driver.fullName}" is currently processing an active order. Are you sure you want to change their status to ${selectedDriverForToggle.targetStatus ? 'Online' : 'Offline'}?`
                            : `Are you sure you want to change driver "${selectedDriverForToggle.driver.fullName}" status to ${selectedDriverForToggle.targetStatus ? 'Online' : 'Offline'}?`
                    }
                    type={
                        (selectedDriverForToggle.driver.driverInfo?.activeOrderCount && selectedDriverForToggle.driver.driverInfo.activeOrderCount > 0)
                            ? 'warning'
                            : 'info'
                    }
                    confirmText={`Set to ${selectedDriverForToggle.targetStatus ? 'Online' : 'Offline'}`}
                    loading={toggleLoadingId === selectedDriverForToggle.driver.id}
                    onConfirm={() => executeOnlineToggle(selectedDriverForToggle.driver, selectedDriverForToggle.targetStatus)}
                />
            )}
        </Layout>
    );
};
